import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { UploadCloud, Loader2, X, ImageIcon } from 'lucide-react';
import { blogService } from '../../services/blogService';

interface ImageUploadProps {
  value?: string;
  onChange: (url: string) => void;
  label?: string;
}

// Web image upload (file picker → S3 via /upload/image), matching the rest of the system.
const ImageUpload: React.FC<ImageUploadProps> = ({ value, onChange, label }) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const pick = () => inputRef.current?.click();

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('blogSeo.upload.onlyImages', 'Selecione um arquivo de imagem.'));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('blogSeo.upload.tooLarge', 'A imagem deve ter no máximo 10MB.'));
      return;
    }
    try {
      setUploading(true);
      const res = await blogService.uploadImage(file);
      if (res?.url) {
        onChange(res.url);
        toast.success(t('blogSeo.upload.success', 'Imagem enviada!'));
      } else {
        throw new Error('no url');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? err?.message ?? t('blogSeo.upload.error', 'Falha no upload da imagem.'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {label && <label className="label">{label}</label>}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />

      {value ? (
        <div className="relative group rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
          {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
          <img src={value} alt="preview" className="w-full h-40 object-cover" />
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={pick}
              disabled={uploading}
              className="px-3 py-1.5 rounded-lg bg-white/90 text-gray-900 text-sm font-medium hover:bg-white inline-flex items-center gap-1.5"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
              {t('blogSeo.upload.change', 'Trocar')}
            </button>
            <button
              type="button"
              onClick={() => onChange('')}
              className="px-3 py-1.5 rounded-lg bg-red-500/90 text-white text-sm font-medium hover:bg-red-500 inline-flex items-center gap-1.5"
            >
              <X className="w-4 h-4" />
              {t('blogSeo.upload.remove', 'Remover')}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={pick}
          disabled={uploading}
          className="w-full flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 bg-gray-50/60 dark:bg-gray-800/40 py-8 text-gray-500 dark:text-gray-400 hover:border-primary-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
        >
          {uploading ? (
            <>
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="text-sm">{t('blogSeo.upload.uploading', 'Enviando...')}</span>
            </>
          ) : (
            <>
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                <ImageIcon className="w-5 h-5" />
              </div>
              <span className="text-sm font-medium">{t('blogSeo.upload.cta', 'Enviar imagem')}</span>
              <span className="text-xs">{t('blogSeo.upload.hint', 'PNG, JPG ou WebP até 10MB')}</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default ImageUpload;
